import React from 'react';

interface Logo7bProps {
  /** Tamanho do tile + texto. Default 'lg' (42px), igual ao template. */
  size?: 'sm' | 'md' | 'lg';
  /** Quando `false`, mostra apenas o tile sem a palavra "barber". */
  withWord?: boolean;
  /**
   * URL de logo customizada (subido pelo dono do tenant ou pelo super-admin
   * no PlatformConfig). Quando definida, substitui o tile "7b" terra +
   * a palavra "7barber" por uma imagem unica. Mantem fallback no logo
   * padrao quando vazia/nula/undefined.
   */
  src?: string | null;
  /**
   * Texto alternativo da imagem. Usado quando `src` esta definido.
   * Tipicamente: nome do tenant ou da plataforma.
   */
  alt?: string;
  /**
   * Subtitulo exibido logo abaixo da palavra (par titulo/subtitulo ao
   * lado do tile). Ex.: "Entre na sua barbearia" no login mobile.
   * Sem `caption` o layout permanece o de sempre (tile + palavra em row).
   */
  caption?: React.ReactNode;
  className?: string;
}

/**
 * Logo "7b" + "barber" em paleta terra para as telas de auth. Difere do
 * `.logo` global (preto + Poppins) - aqui o tile e gradient terra com
 * "7b" Instrument Serif italic.
 *
 * Quando recebe `src`, renderiza a logo customizada do tenant/plataforma
 * mantendo o mesmo tamanho do tile padrao (sm 32px / md 36px / lg 42px),
 * com object-fit cover e radius do tema. Sem `src`, renderiza o "7b" terra
 * + "7barber" padrao.
 *
 * So renderiza corretamente dentro de `<AuthShell>` (precisa dos tokens
 * --brand-500/--text-1 e da fonte --auth-font-serif).
 *
 * Origem: templates/Login com Google/src/brand.jsx <Logo7b>.
 */
export function Logo7b({
  size = 'lg',
  withWord = true,
  src,
  alt = '7barber',
  caption,
  className,
}: Logo7bProps) {
  const root = ['auth-logo', `auth-logo--${size}`, className]
    .filter(Boolean)
    .join(' ');

  // Palavra: nome do tenant no estilo base; a palavra da PLATAFORMA usa a
  // wordmark OFICIAL ("7" Playfair dourado + "barber" Oswald - brand-spec).
  const word = src ? (
    withWord && alt ? <span className="auth-logo__word">{alt}</span> : null
  ) : withWord ? (
    <span className="auth-logo__word auth-logo__word--brand">
      <span className="auth-logo__word-seven">7</span>barber
    </span>
  ) : null;

  // Com caption, palavra + subtitulo viram uma coluna ao lado do tile
  // (par titulo/subtitulo). Sem caption, a palavra segue direto na row.
  const text =
    word && caption ? (
      <span className="auth-logo__text">
        {word}
        <span className="auth-logo__caption">{caption}</span>
      </span>
    ) : (
      word
    );

  if (src) {
    return (
      <span className={root} aria-label={alt}>
        <img
          src={src}
          alt=""
          className="auth-logo__image"
          loading="eager"
          decoding="async"
        />
        {text}
      </span>
    );
  }

  // Default (sem logo customizada): o ICONE OFICIAL da marca
  // (brand/catalog.html - tile escuro + "7" dourado, espelhado em
  // /logo-7barber.svg). O antigo tile "7b" nao era a marca (corrigido
  // a pedido do operador em 2026-07-07).
  return (
    <span className={root} aria-label="7barber">
      <img
        src="/logo-7barber.svg"
        alt=""
        className="auth-logo__image"
        loading="eager"
        decoding="async"
      />
      {text}
    </span>
  );
}
